import { Controller } from "@hotwired/stimulus";
import RequestHelper from "../helpers/request_helper";
import DesignUploaderHelper from "../helpers/design_uploader_helper";

// Drives gives/designs/show.html.haml. Uses DesignUploaderHelper for the upload plumbing shared
// with the checkout/redemption flows.
//
// The design tray container is a turbo_frame_tag, shared with the checkout and redemption
// mounts. Tray links sit inside it, so the frame drives them with no per-link target.
//
// Not covered by spec/features/gives/give_design_spec.rb, being undrivable under headless
// Capybara: the spectrum color picker, the atwho "@"-mention autocomplete, the plain-text paste
// guard, and the breakpoint-gated mobile tokens tray. The message and signature entry path is
// covered end-to-end by spec/features/organizations/gift_content_and_templates_spec.rb.
const MOBILE_BREAKPOINT = 1180;

const DESIGN_FRAME_ID = "give-frame";

// fetchMessageTray/toggleEmailSettings navigate the tray frame programmatically. Turbo.visit
// with a `frame` option sets that frame's src; real link clicks are driven by the frame itself.
// Turbo is loaded as a UMD bundle through sprockets, so it is a global here, not an import.
const visitInDesignFrame = (url) => {
  window.Turbo.visit(url, { frame: DESIGN_FRAME_ID });
};

const MENU_TITLES = {
  logos: { main: "Edit logo" },
  images: { main: "Image will apply to desktop & mobile banner" },
  emailSettings: { main: "Email settings" },
  messages: { main: "Previous messages" },
  preview: { main: "Email preview", subtext: "This is a non-interactive preview.", icon: "icon-lasso-info" }
};

export default class extends Controller {
  static get values() {
    return {
      messagesPath: String,
      emailSettingsPath: String,
      defaultPath: String
    };
  }

  connect() {
    this.mobileOnlyMediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    this.mobileMenu = this.element.querySelector(".designs-column.mobile-menu");
    this.debouncedUpdateColorInBackend = _.debounce((fn) => fn(), 500);

    // gives/_progress.html.haml is shared by several controllers and has no per-caller slot for
    // a data-action, so #next is handled by delegation here - covering both that link and this
    // page's own mobile-actions duplicate.
    this.onNextClick = this.onNextClick.bind(this);
    this.element.addEventListener("click", this.onNextClick);

    // turbo:before-fetch-request is the pjax:beforeSend equivalent and turbo:frame-load the
    // pjax:end one. Both fire on the frame, but the frame element can be replaced, so these are
    // delegated off this.element and filtered by id rather than bound to a reference captured
    // here -- a captured reference goes stale on replacement and the listener silently stops
    // firing, leaving the loading class (opacity 0, pointer-events none) stuck on (P8).
    this.onFrameFetchRequest = this.onFrameFetchRequest.bind(this);
    this.onDesignFrameLoad = this.onDesignFrameLoad.bind(this);

    this.element.addEventListener("turbo:before-fetch-request", this.onFrameFetchRequest);
    this.element.addEventListener("turbo:frame-load", this.onDesignFrameLoad);

    // turbo:frame-load only fires after a successful render, so a request that fails outright
    // clears the loading class here or the tray stays invisible.
    this.onFrameFetchError = this.onFrameFetchError.bind(this);
    this.element.addEventListener("turbo:fetch-request-error", this.onFrameFetchError);

    this.onBuildFromPaste = this.onBuildFromPaste.bind(this);
    this.buildFromEl = this.element.querySelector("#build_from");
    // Card Signature is not a normal textarea, this is to enforce the pasting of plain text into
    // it. https://stackoverflow.com/questions/58980235
    this.buildFromEl?.addEventListener("paste", this.onBuildFromPaste);

    // mousedown fires before blur, so this flag is set by the time triggerMessageBlur reads it.
    // relatedTarget cannot be used instead - it comes back null on this particular blur. Delegated
    // on this.element because the messages tray is pjax-swapped content.
    this.onPreviousMessageMousedown = this.onPreviousMessageMousedown.bind(this);
    this.element.addEventListener("mousedown", this.onPreviousMessageMousedown);

    DesignUploaderHelper.initialize();

    $(this.element).find("#build_message").atwho({
      at: ":",
      data: ["name", "first_name", "last_name", "email"].map((value) => ({ key: value, name: value })),
      insertTpl: ":${key}:"
    });

    this.initializePreviewUpdaters();

  }

  disconnect() {
    this.element.removeEventListener("click", this.onNextClick);
    this.buildFromEl?.removeEventListener("paste", this.onBuildFromPaste);
    this.element.removeEventListener("mousedown", this.onPreviousMessageMousedown);

    this.element.removeEventListener("turbo:before-fetch-request", this.onFrameFetchRequest);
    this.element.removeEventListener("turbo:frame-load", this.onDesignFrameLoad);
    this.element.removeEventListener("turbo:fetch-request-error", this.onFrameFetchError);
  }

  onDesignFrameLoad(event) {
    if (event.target.id !== DESIGN_FRAME_ID) { return; }

    this.removeLoadingState();

    $.smoothScroll({ scrollTarget: `#${DESIGN_FRAME_ID}`, offset: -200 });

    if ($(".email-settings").length) {
      this.initializeSpectrum();
      this.initializePreviewUpdaters();
    }

    if (this.isMobileVersionEnabled() && $(this.element).find(".tips-and-tricks").length) {
      this.checkMessageTruncation();
    }
  }

  // --- helpers ---

  isMobileVersionEnabled() {
    return this.mobileOnlyMediaQuery.matches;
  }

  getMobileTokensSection() {
    return this.element.querySelector(".mobile-tokens-and-previous-messages");
  }

  addLoadingState() {
    const container = document.querySelector("#give-frame");
    if (container) { container.classList.add("loading"); }
  }

  removeLoadingState() {
    const container = document.querySelector("#give-frame");
    if (container) { container.classList.remove("loading"); }
  }

  onFrameFetchRequest(event) {
    if (event.target.id !== DESIGN_FRAME_ID) { return; }

    this.addLoadingState();
  }

  onFrameFetchError(event) {
    if (event.target.id !== DESIGN_FRAME_ID) { return; }

    this.removeLoadingState();
  }

  onBuildFromPaste(e) {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, pastedText);
  }

  // Covers all three previous-message row shapes, desktop and mobile.
  onPreviousMessageMousedown(event) {
    this.selectingPreviousMessage = !!event.target.closest(".previous-message, .previous-message-container, .option.message");
  }

  onNextClick(event) {
    const nextButton = event.target.closest("#next");
    if (!nextButton) { return; }

    this.nextStep(event);
  }

  mobileMenuIsOpen() {
    return !!document.querySelector(".mobile-menu.open");
  }

  // --- design uploader ---

  showNewUpload() {
    DesignUploaderHelper.showNewUpload(this.mobileMenuIsOpen());
  }

  hideNewUpload() {
    DesignUploaderHelper.hideNewUpload();
  }

  // --- color picker (spectrum) ---

  initializeSpectrum() {
    const controller = this;

    $(".email-settings .setting-color-picker").spectrum({
      type: "text",
      preferredFormat: "hex",
      showInput: true,
      showPalette: true,
      palette: [
        ["#333333", "#555555", "#777777", "#1377CD", "#585D85", "#213752", "#87A950", "#448475", "#004F61"],
        ["#EE9911", "#FFD061", "#FFC692", "#FF7755", "#CF1322", "#F6737E", "#855ECB", "#8285A3", "#BCEFFF"]
      ],
      showAlpha: false,
      allowEmpty: false,
      // Plain functions, not arrows: spectrum calls these with `this` set to the picker element.
      // `controller` closes over the class instance.
      move: function(color) { controller.updateColor($(this), color, true); },
      show: function() { $(this).spectrum("set", $(this).find(".color-code").text()); }
    });
  }

  updateColor($picker, color, sendToBackend) {
    const $previewTarget = $($picker.data("target"));
    const previewTargetAttr = $picker.data("attr");

    $($picker.find(".color-sample")).css("background-color", color.toHexString());
    $picker.find(".color-code").text(color.toHexString().toUpperCase());
    $previewTarget.css(previewTargetAttr, color.toHexString());

    if (sendToBackend) {
      this.updateColorInBackend(previewTargetAttr === "background-color" ? "button_color" : "button_text_color", color.toHexString().toUpperCase());
    }
  }

  updateColorInBackend(attr, colorCode) {
    this.debouncedUpdateColorInBackend(() => {
      const path = $(this.element).find("#build_subject").parents("form").first().attr("action");
      const options = RequestHelper.defaultPatchOptions();
      options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
      options.body = JSON.stringify({ build: { [attr]: colorCode } });

      fetch(path, options).then((response) => {
        if (!response.ok) { console.warn(response); }
      }).catch((error) => console.warn(error));
    });
  }

  // --- preview updaters (message/subject/cta preview mirrors + live email-settings autosave) ---

  initializePreviewUpdaters() {
    document.querySelectorAll(".preview-updater").forEach((previewUpdater) => {
      previewUpdater.addEventListener("input", (e) => {
        const input = e.target;
        const updateTargets = document.querySelectorAll(input.dataset.target);
        const newValue = input.value || input.textContent;

        updateTargets.forEach((target) => { target.textContent = newValue; });
      });
    });

    document.querySelectorAll(".email-settings input").forEach((input) => {
      input.addEventListener("change", () => {
        const path = $(this.element).find("#build_subject").parents("form").first().attr("action");
        const options = RequestHelper.defaultPatchOptions();
        options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
        options.body = JSON.stringify({
          build: {
            subject: $(this.element).find("#build_subject").val(),
            call_to_action: $(this.element).find("#build_call_to_action").val(),
            cc_email: $(this.element).find("#build_cc_email").val(),
            display_custom_footer: $(this.element).find("#build_display_custom_footer").is(":checked")
          }
        });

        fetch(path, options).then((response) => {
          if (!response.ok) { console.warn(response); }
        }).catch((error) => console.warn(error));
      });
    });
  }

  // --- actions (data-action targets) ---

  nextStep(e) {
    e.preventDefault();
    $(this.element).find("form#giving-form").submit();
  }

  fetchMessageTray(e) {
    if (this.isMobileVersionEnabled()) {
      this.getMobileTokensSection().classList.add("active");
    } else {
      if ($(".email-settings-wrapper.active").length) {
        this.toggleEmailSettings(e);
      }

      visitInDesignFrame(this.messagesPathValue);
    }
  }

  removeError(event) {
    let delegateTarget;
    if (event instanceof HTMLElement) {
      delegateTarget = event;
    } else {
      // delegateTarget is a jQuery-delegation-only property that a raw keyup Event never has, so
      // this branch is inert on the keyup path. Pre-existing, left as-is.
      delegateTarget = event.delegateTarget;
    }

    $(this.element).find(delegateTarget).parent(".form-group").removeClass("has-error");
    $(this.element).find(delegateTarget).parent(".form-group").find(".help-block").addClass("hidden");
  }

  updateSubjectPreview(event) {
    const isSubjectOverriden = event.target.dataset.overriden === "true";

    if (isSubjectOverriden) { return false; }

    const input = event.target;
    const subjectInput = document.getElementById("build_subject");
    const subjectPreview = document.querySelector(".email-preview-subject");
    const defaultSubject = " Sent You a Gift!";

    if (subjectInput) { subjectInput.value = input.textContent + defaultSubject; }
    if (subjectPreview) { subjectPreview.textContent = input.textContent + defaultSubject; }
  }

  markAsOverridden() {
    document.querySelector(".polaroid-form #build_from").dataset.overriden = true;
  }

  triggerMessageBlur(evt) {
    // A blur caused by clicking a previous-message row would race usePreviousBuildMessage's own
    // writeCardText() with a stale payload, so skip it and let that one win.
    const skipWrite = this.selectingPreviousMessage;
    this.selectingPreviousMessage = false;

    if (!skipWrite) {
      this.writeCardText();
    }

    if (this.isMobileVersionEnabled() && !this.getMobileTokensSection().contains(evt.relatedTarget)) {
      setTimeout(() => {
        this.getMobileTokensSection().classList.remove("active");
      }, 150);
    }
  }

  writeCardText(evt) {
    // Focus moving from #build_from straight to #build_message fires this with a stale message
    // value; #build_message always gets a later write with both fields final, so drop this one.
    // evt is undefined for every other caller, so they are unaffected.
    if (evt && evt.relatedTarget && evt.relatedTarget.id === "build_message") { return; }

    const path = $(this.element).find("#build_message").parents("form").first().attr("action");
    const options = RequestHelper.defaultPatchOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify({
      build: {
        message: $(this.element).find("#build_message").val(),
        from: $(this.element).find("#build_from").text()
      }
    });
    // Do NOT follow the redirect. Navigating away blurs #build_from, firing this autosave during
    // teardown; following the 302 would GET /give/design, whose ensure_correct_step_controller
    // rewrites the build's step BACKWARDS and drops the user on the page they just left.
    options.redirect = "manual";

    fetch(path, options).then((response) => {
      if (!response.ok) { console.warn(response); }
    }).catch((error) => console.warn(error));
  }

  insertToken(event) {
    const token = event.params.token;
    const message = document.getElementById("build_message");
    const cursorPosition = message.selectionStart;
    const messageText = message.value;
    const newMessageText = messageText.slice(0, cursorPosition) + ` ${token} ` + messageText.slice(cursorPosition);

    message.value = newMessageText;
    message.focus();
  }

  usePreviousBuildMessage($event) {
    const messageInput = document.getElementById("build_message");
    const message = $event.target;

    messageInput.value = message.textContent.trim();
    // Not autosize.update(): the bare global is Sprockets' copy, whose Map never saw this textarea
    // (resize-textarea's webpack copy initialized it). autosize listens for this event on the node.
    messageInput.dispatchEvent(new Event("autosize:update"));

    $(".previous-messages .previous-message-container.selected").removeClass("selected");
    message.parentElement.classList.add("selected");
    this.removeError(messageInput);
    this.writeCardText();
    this.toggleMobileMenu({ params: { option: "close" } });
    this.getMobileTokensSection().classList.remove("active");
  }

  toggleEmailSettings(e) {
    e.stopPropagation();

    const emailSettingsWrapper = document.querySelector(".card-column .email-settings-wrapper");
    const emailPreviewWrappers = document.querySelectorAll(".email-preview-wrapper");
    const toggleEmailSettingsBtn = document.querySelector(".btn-email-settings");
    const buttonText = toggleEmailSettingsBtn.querySelector(".btn-label").textContent;

    let url;

    if (buttonText === "Email Settings") {
      toggleEmailSettingsBtn.querySelector(".btn-label").textContent = "Hide Email Settings";
      emailSettingsWrapper.classList.add("active");
      emailPreviewWrappers.forEach((wrapper) => { wrapper.classList.add("active"); });

      url = this.emailSettingsPathValue;
    } else {
      toggleEmailSettingsBtn.querySelector(".btn-label").textContent = "Email Settings";
      emailSettingsWrapper.classList.remove("active");
      emailPreviewWrappers.forEach((wrapper) => { wrapper.classList.remove("active"); });

      url = this.defaultPathValue;
    }

    visitInDesignFrame(url);
  }

  updateHideLogoInBackend() {
    const path = $(this.element).find("#giving-form").attr("action");
    const options = RequestHelper.defaultPatchOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify({ build: { hide_logo: $(this.element).find("#build_hide_logo").val() } });

    fetch(path, options).then((response) => {
      if (!response.ok) { console.warn(response); }
    }).catch((error) => console.warn(error));
  }

  hideLogo(e) {
    e.preventDefault();
    e.stopPropagation();

    document.querySelector("#giving-form #build_hide_logo").value = true;
    document.querySelector(".show-logo-container").classList.remove("hide");
    document.querySelector(".hover-logo").classList.add("hide");

    if (this.isMobileVersionEnabled()) {
      document.querySelector(".btn-hide-logo-mobile").classList.add("hide");
      document.querySelector(".btn-show-logo-mobile").classList.remove("hide");
      this.toggleMobileMenu({ params: { option: "close" } });
    }

    this.updateHideLogoInBackend();
  }

  showLogo(e) {
    e.preventDefault();

    document.querySelector("#giving-form #build_hide_logo").value = false;
    document.querySelector(".show-logo-container").classList.add("hide");
    document.querySelector(".hover-logo").classList.remove("hide");

    if (this.isMobileVersionEnabled()) {
      document.querySelector(".btn-hide-logo-mobile").classList.remove("hide");
      document.querySelector(".btn-show-logo-mobile").classList.add("hide");
      this.toggleMobileMenu({ params: { option: "close" } });
    }

    this.updateHideLogoInBackend();
  }

  updateHiddenField(source, target) {
    document.querySelector(target).value = source.value || source.textContent;
  }

  fromKeyUp(e) {
    this.removeError(e);
    this.updateSubjectPreview(e);
    this.updateHiddenField(e.target, "#hidden-from-field");
  }

  subjectKeyUp(e) {
    this.markAsOverridden();
    document.querySelector("#hidden-subject-field").disabled = false;
    this.updateHiddenField(e.target, "#hidden-subject-field");
  }

  ccEmailKeyup(e) {
    this.markAsOverridden();
    document.querySelector("#hidden-cc-email-field").disabled = false;
    this.updateHiddenField(e.target, "#hidden-cc-email-field");
  }

  validateCCEmail(e) {
    const input = e.target;
    const email = input.value.trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const errorElement = document.querySelector(".send-on-behalf-of .email-error");

    if (!email.length) {
      errorElement.classList.add("hidden");
      return true;
    }

    if (emailPattern.test(email)) {
      errorElement.classList.add("hidden");
      return true;
    } else {
      errorElement.classList.remove("hidden");
      return false;
    }
  }

  ctaKeyUp(e) {
    document.querySelector("#hidden-cta-field").disabled = false;
    this.updateHiddenField(e.target, "#hidden-cta-field");
  }

  triggerEditLogoBtn() {
    document.querySelector(".btn-edit-logo")?.click();
  }

  triggerEditImageBtn() {
    document.querySelector(".btn-edit-card-image")?.click();
  }

  setupMobileMenuHeader(option) {
    const mobileMenuHeader = document.querySelector(".designs-column.mobile-menu .mobile-menu-header");
    const mainText = mobileMenuHeader.querySelector(".main-text");
    const subText = mobileMenuHeader.querySelector(".sub-text");
    const iconContainer = mobileMenuHeader.querySelector(".icon");
    const icon = iconContainer.querySelector("i");

    subText.textContent = "";
    iconContainer.classList.remove("active");
    icon.className = "";
    mainText.textContent = MENU_TITLES[option].main;

    if (MENU_TITLES[option].subtext) { subText.textContent = MENU_TITLES[option].subtext; }
    if (MENU_TITLES[option].icon) {
      iconContainer.classList.add("active");
      icon.classList.add(MENU_TITLES[option].icon);
    }
  }

  toggleMobileMenu(event) {
    const option = event.params.option;
    const mobileMenuContent = document.querySelector(".designs-column.mobile-menu .content");

    if (option === "close") {
      this.mobileMenu.classList.remove("open");
      mobileMenuContent.classList.remove("active");
    } else {
      if (!this.mobileMenu.classList.contains("open")) {
        this.mobileMenu.classList.add("open");
      }

      this.setupMobileMenuHeader(option);
      mobileMenuContent.classList.add("active");
    }
  }

  toggleAccordion(event) {
    const accordion = event.currentTarget.parentElement;

    document.querySelectorAll(".accordion.open").forEach((openAccordion) => {
      if (openAccordion !== accordion) { openAccordion.classList.remove("open"); }
    });

    accordion.classList.toggle("open");
  }

  toggleMessageExpansion(event) {
    event.stopPropagation();

    const button = event.currentTarget;
    const container = button.parentElement;
    const message = container.querySelector(".previous-message");

    message.classList.toggle("expanded");
    message.classList.toggle("collapsed");
    container.classList.toggle("expanded-message");

    const textNode = Array.from(button.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
    if (message.classList.contains("expanded")) {
      textNode.textContent = "See less ";
    } else {
      textNode.textContent = "See more ";
    }
  }

  checkMessageTruncation() {
    setTimeout(() => {
      document.querySelectorAll(".previous-message-container").forEach((container) => {
        const message = container.querySelector(".previous-message");

        if (message && message.scrollHeight > message.clientHeight) {
          container.classList.add("has-more");
        }
      });
    }, 0);
  }
}
